import { icon } from '../../icons';
import { type TenantDto } from '../../services/api';
import { i18nService, t } from '../../services/i18n';

export type CandidateStatusConfig = {
  key: string;
  label: string;
  color: string;
  icon: string;
};

export function getCandidateStatuses(): CandidateStatusConfig[] {
  return [
    { key: 'Kandidaat', label: t('waitinglist.status.candidate'), color: '#3b82f6', icon: icon('user', { size: 14 }) },
    { key: 'Geselecteerd', label: t('waitinglist.status.selected'), color: '#f59e0b', icon: icon('star', { size: 14 }) },
    { key: 'Afgewezen', label: t('waitinglist.status.rejected'), color: '#ef4444', icon: icon('x', { size: 14 }) },
    { key: 'Actief', label: t('waitinglist.status.activeTenant'), color: '#10b981', icon: icon('check-circle', { size: 14 }) },
  ];
}

export function translateCandidateStatus(status: string): string {
  return getCandidateStatuses().find((candidateStatus) => candidateStatus.key === status)?.label ?? status;
}

export const NEXT_STATUS: Record<string, string> = {
  Kandidaat: 'Geselecteerd',
  Geselecteerd: 'Actief',
};

export const EMPTY_PROPERTY_ICON = icon('home', { size: 48, strokeWidth: 1.2, className: 'waitinglist-icon-empty' });
export const EMPTY_CANDIDATE_ICON = icon('users', { size: 48, strokeWidth: 1.2, className: 'waitinglist-icon-empty' });
export const PLUS_ICON = icon('plus', { size: 15, strokeWidth: 2.5 });
export const PROPERTY_TAB_ICON = icon('home', { size: 12 });
export const PROPERTY_BANNER_ICON = icon('home', { size: 18 });
export const MAIL_ICON = icon('mail', { size: 10 });
export const ALERT_ICON = icon('alert-circle', { size: 9 });
export const REFRESH_ICON = icon('refresh-ccw', { size: 11 });
export const TRASH_ICON = icon('trash', { size: 11 });
export const PHONE_ICON = icon('phone', { size: 10 });
export const CALENDAR_ICON = icon('calendar', { size: 10 });
export const CLOSE_ICON = icon('x', { size: 20 });
export const INFO_ICON = icon('alert-circle', { size: 16, attrs: 'style="flex-shrink:0;margin-top:1px"' });

const PORTAL_STATUS_ICONS: Record<string, string> = {
  'has-account-unlinked': icon('user', { size: 11 }),
  'no-account-unlinked': icon('alert-circle', { size: 11 }),
  'has-account-linked': icon('check-circle', { size: 11 }),
  'no-account-invite-sent': icon('mail', { size: 11 }),
};

const PORTAL_STATUS_BADGES: Record<string, string> = {
  'has-account-unlinked': 'badge-warning',
  'no-account-unlinked': 'badge-muted',
  'has-account-linked': 'badge-success',
  'no-account-invite-sent': 'badge-info',
};

export type CandidatePortalStatusConfig = {
  key: string;
  label: string;
  tooltip: string;
  badgeClass: string;
  icon: string;
};

export function getCandidatePortalStatusConfig(status?: string): CandidatePortalStatusConfig {
  const key = status ?? 'no-account-unlinked';
  return {
    key,
    label: t(`waitinglist.portalStatus.${key}.label`),
    tooltip: t(`waitinglist.portalStatus.${key}.tooltip`),
    badgeClass: PORTAL_STATUS_BADGES[key] ?? 'badge-muted',
    icon: PORTAL_STATUS_ICONS[key] ?? icon('alert-circle', { size: 11 }),
  };
}

export function canSendCandidateInvite(candidate: Pick<TenantDto, 'email' | 'tenantPortalConnectionStatus'>): boolean {
  const portalStatus = candidate.tenantPortalConnectionStatus ?? 'no-account-unlinked';
  return !!candidate.email
    && (portalStatus === 'no-account-unlinked'
      || portalStatus === 'no-account-invite-sent');
}

export function formatRentomaxProvider(provider?: string): string {
  if (provider?.toLowerCase() === 'google') return 'Google';
  if (provider?.toLowerCase() === 'microsoft') return 'Microsoft';
  if (provider?.toLowerCase() === 'email') return 'Email';
  return 'Rentomax';
}

export function esc(str: string): string {
  const el = document.createElement('span');
  el.textContent = str;
  return el.innerHTML;
}

export function formatDate(dateStr?: string): string {
  if (!dateStr) return '-';
  return i18nService.formatDate(`${dateStr}T00:00:00`, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function isExpiredConverted(candidate: TenantDto): boolean {
  return candidate.status === 'Actief' && !!candidate.endDate && new Date(candidate.endDate) < new Date();
}
